import {
  TemplateVariableType,
  type TemplateVariable,
} from "@/shared/domain/agents";
import { type TriggerTemplateCatalog } from "@/shared/domain/triggers";

/**
 * Catalog paths that answer a typed variable, in preference order. The
 * launch page seeds a `dataset_id` variable from the page's dataset; here the
 * dataset is the event's, so the seed is the placeholder that resolves to it.
 */
const PATHS_BY_TYPE: Partial<Record<TemplateVariableType, readonly string[]>> =
  {
    [TemplateVariableType.DATASET_ID]: ["dataset.dataset_id"],
    [TemplateVariableType.DEVICE_ID]: ["dataset.device_id", "file.device_id"],
  };

/**
 * The placeholder a variable should start with, or `null`. A typed variable
 * takes the path that carries its kind; a plain string variable takes the
 * catalog path whose last segment is the variable's own name (`session_id`
 * finds `session.session_id`), since that is what the author almost always
 * means. Only paths the subscribed events actually expose are offered, so a
 * prefilled placeholder always resolves.
 */
export function prefillPlaceholder(
  variable: TemplateVariable,
  catalog: TriggerTemplateCatalog,
): string | null {
  const typed = PATHS_BY_TYPE[variable.type] ?? [];
  const byType = typed.find((path) => catalog.has(path));
  if (byType !== undefined) {
    return `{{${byType}}}`;
  }
  const byName = catalog.paths.find(
    (entry) =>
      !entry.container && entry.path.split(".").at(-1) === variable.name,
  );
  return byName === undefined ? null : `{{${byName.path}}}`;
}

/**
 * Starting values for an agent's variables when it is chosen for a trigger:
 * every variable the catalog can answer is prefilled, the rest are left for
 * the author.
 */
export function prefillAgentValues(
  variables: readonly TemplateVariable[],
  catalog: TriggerTemplateCatalog,
): Record<string, string> {
  const values: Record<string, string> = {};
  for (const variable of variables) {
    const placeholder = prefillPlaceholder(variable, catalog);
    if (placeholder !== null) {
      values[variable.name] = placeholder;
    }
  }
  return values;
}
